import { AxiosError } from "axios";
import { AlertCircle, LoaderCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiRoute from "routes/apirRoutes";
import http from "services/httpService";
import useSWR from "swr";

export function VerifyNINForm() {

  const [firstname,setFirstname] = useState("");
  const [lastname,setLastname] = useState("");
  const [nin, setNin] = useState("");
  const [loading, setLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const fetcher = async () => {
		const res = await http.get(apiRoute.whoIAm)
		return res?.data?.data
	}

	const { data: userProfile } = useSWR(apiRoute.whoIAm, fetcher)

  useEffect(()=>{
    if(userProfile){
      setFirstname(userProfile?.first_name)
      setLastname(userProfile?.surname)
    }
  },[userProfile])


  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent re-submission if already successful or loading
    if (loading || isSuccess) return;

    setLoading(true);
    setIsError(false);
    setErrorMessage("");

    try {
      // 1. Improved Validation
      if (nin.length !== 11) {
        throw new Error("NIN must be exactly 11 digits");
      }
      if (!/^\d+$/.test(nin)) {
        throw new Error("NIN must contain only numbers");
      }
      if (!firstname) {
        throw new Error("Firstname is required");
      }
      if (!lastname) {
        throw new Error("Lastname is required");
      }

      const { data } = await http.patch(apiRoute.verifyUserNin, { nin,firstname,lastname });

      // Note: axios usually throws on 4xx/5xx, but if your API 
      // returns 200 with an error flag, this handles it:
      if (data.error) {
        throw new Error(data.message || "Verification failed");
      }

      setIsSuccess(true);
      
      // Navigate after success
      setTimeout(() => {
        navigate("/dashboard/profile");
      }, 5000);

    } catch (error) {
      setIsError(true);
      // Handle Axios vs Manual Error vs Generic Error
      const msg = error instanceof AxiosError 
        ? error.response?.data?.message 
        : error.message;
      
      setErrorMessage(msg || "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-[450px] space-y-4 w-full bg-white p-4 mx-auto">
      <header>
        <p className="font-poppins text-primary text-xl font-semibold capitalize">
          NIN Verification
        </p>
      </header>

      {isSuccess && (
        <div className="bg-green-100 border border-green-300 border-solid mt-2 rounded-lg px-3 py-2">
          <p className="font-poppins text-green-700 text-base font-semibold capitalize">
            Verification Successful
          </p>
          <p className="font-poppins text-black/60 font-medium text-sm">
            Your NIN has been successfully verified. You can now proceed to
            apply for loans.
          </p>
        </div>
      )}

      {(isError || errorMessage) && (
         <div className="bg-red-100 border border-red-200 border-solid mt-2 rounded-lg px-3 py-2">
           <p className="font-poppins text-red-500 text-base font-semibold capitalize">
          Verification Failed
        </p>
          <p className="font-poppins text-red-500 font-medium text-sm">
            {errorMessage}
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="my-8 space-y-4">
        <div>
          <p className="text-sm font-medium font-poppins">First Name</p>
          <input
          onChange={(e) => setFirstname(e.target.value)}
          type="text"
          value={firstname}
          placeholder="Please enter your firstname"
          className="w-full border border-solid bg-primary-100 border-primary-100 rounded-lg px-3 py-2"
        />
        </div>
       <div>
        <p className="text-sm font-medium font-poppins">Last Name</p>
         <input
          onChange={(e) => setLastname(e.target.value)}
          type="text"
          value={lastname}
          placeholder="Please enter your lastname"
          className="w-full border border-solid bg-primary-100 border-primary-100 rounded-lg px-3 py-2"
        />
       </div>
       <div>
        <p className="text-sm font-medium font-poppins">NIN</p>
         <input
          onChange={(e) => setNin(e.target.value)}
          maxLength={11}
          type="text"
          value={nin}
          placeholder="Please enter your NIN"
          className="w-full border border-solid bg-primary-100 border-primary-100 rounded-lg px-3 py-2"
        />
       </div>
        <div className="bg-blue-100 mt-2 flex items-center gap-x-2 rounded-lg px-3 py-2">
          <AlertCircle className="text-blue-500" />
          <p className="font-poppins text-black/60 font-medium text-xs">
            Your name will be automatically updated to match your NIN records.
            After verification, some profile details may no longer be editable.
          </p>
        </div>
        {!userProfile?.nin_verified && (
          <button
          disabled={userProfile?.nin_verified  || !firstname || !lastname || !nin || loading}
          className="w-full inline-flex disabled:opacity-60 items-center gap-x-2  justify-center font-medium bg-primary text-white rounded-lg px-3 py-2"
        >
          <span>VERIFY</span>
          {loading && <LoaderCircle className="animate-spin" />}
        </button>
        )}
      </form>
    </div>
  );
}
